import React, { useContext, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import usersRequest from "../../request/usersRequest";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import "./registerPage.css";
import TermsOfServiceModal from "../common/termsOfServiceModal/termsOfServiceModal";
import PasswordField from "../common/passwordField/passwordField";

const RegisterPage = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [activeModal, setActiveModal] = useState(null);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const { language, setUserData } = useContext(CombinedContext);
    const navigate = useNavigate();

    function isStrongPassword(password) {
        return (
            typeof password === "string" &&
            password.length >= 8 &&
            /[A-Z]/.test(password) &&
            /[0-9]/.test(password) &&
            /\./.test(password)
        );
    }

    const handleRegister = async () => {
        setError("");
        setLoading(true);

        if (!isStrongPassword(password)) {
            setError(
                language === "es"
                    ? "La contraseña debe tener al menos 8 caracteres, una mayúscula, un número y un punto (.)"
                    : "Password must be at least 8 characters and include an uppercase letter, a number, and a period (.)"
            );
            return;
        }

        if (password !== confirmPassword) {
            setError("error", language === "es" ? "Las contraseñas no coinciden" : "Passwords do not match");
            return;
        }

        try {
            // ✅ 1. Call backend
            const res = await usersRequest.register({
                email,
                password,
                firstName,
                lastName,
                acceptedTermsVersion: "1.0",
            });

            // ✅ 2. Format response like admin logic
            const formattedData = {
                user: res.user,
                appToken: res.appToken,
                cognitoTokens: {
                    IdToken: res.cognitoTokens?.IdToken || res.IdToken,
                    AccessToken: res.cognitoTokens?.AccessToken || res.AccessToken,
                    RefreshToken: res.cognitoTokens?.RefreshToken || res.RefreshToken,
                },
            };

            // ✅ 3. Save to global context
            setUserData(formattedData);

            // ✅ 4. Persist to storage
            const storage = rememberMe ? localStorage : sessionStorage;

            storage.setItem("user", JSON.stringify(formattedData.user));
            if (formattedData.appToken) storage.setItem("appToken", formattedData.appToken);
            if (formattedData.cognitoTokens?.IdToken) storage.setItem("idToken", formattedData.cognitoTokens.IdToken);
            if (formattedData.cognitoTokens?.AccessToken)
                storage.setItem("accessToken", formattedData.cognitoTokens.AccessToken);
            if (formattedData.cognitoTokens?.RefreshToken)
                storage.setItem("refreshToken", formattedData.cognitoTokens.RefreshToken);

            // ✅ 5. Redirect like login
            navigate("/");
        } catch (err) {
            console.error("Registration failed:", err);

            if (err.response?.data?.details?.includes("UsernameExistsException")) {
                setError(language === "es" ? "Este correo ya está en uso" : "Email already in use");
            } else {
                setError(translations[language].register.error);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container">
            <div className="card" style={{ maxWidth: 400, margin: "40px auto" }}>
                <div className="hd">
                    <div>{translations[language].register.title}</div>
                </div>
                <div className="bd">
                    <div className="row">
                        <input
                            className="input"
                            type="email"
                            placeholder={translations[language].register.email}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <PasswordField
                            value={password}
                            onChange={setPassword}
                            placeholder={translations[language].register.password}
                            autoComplete="new-password"
                        />

                        <PasswordField
                            value={confirmPassword}
                            onChange={setConfirmPassword}
                            placeholder={language === "es" ? "Confirmar contraseña" : "Confirm password"}
                            autoComplete="new-password"
                        />

                        <input
                            className="input"
                            placeholder={translations[language].register.firstName}
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                        <input
                            className="input"
                            placeholder={translations[language].register.lastName}
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                    </div>

                    {/* ✅ Optional Remember Me like login */}
                    <div style={{ marginBottom: 10 }}>
                        <label style={{ fontSize: 14, cursor: "pointer" }}>
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                style={{ marginRight: 6 }}
                            />
                            {language === "es" ? "Recordarme" : "Remember me"}
                        </label>
                    </div>

                    <button
                        className="btn brand"
                        style={{ width: "100%", marginBottom: 10 }}
                        onClick={handleRegister}
                        disabled={loading}
                    >
                        {loading ? translations[language].register.loading : translations[language].register.submit}
                    </button>

                    <div className="terms-checkbox">
                        <label className="terms-label">
                            <input
                                type="checkbox"
                                checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                            />
                            <span>
                                {translations[language].register.acceptTerms}{" "}
                                <span className="sf-terms-span" onClick={() => setActiveModal("terms")}>
                                    {translations[language].register.termsLink}
                                </span>
                            </span>
                        </label>
                    </div>

                    {error && (
                        <div className="sub" style={{ color: "red" }}>
                            {error}
                        </div>
                    )}

                    <div className="sub">
                        {translations[language].register.haveAccount}{" "}
                        <Link to="/signin">{translations[language].register.signIn}</Link>
                    </div>
                </div>
            </div>
            {activeModal === "terms" && <TermsOfServiceModal isOpen onClose={() => setActiveModal(null)} />}
        </div>
    );
};

export default RegisterPage;
